/**
 * Preset Bundles Browser
 * Browse and purchase preset packs/bundles from the community
 */

import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import {
  Package,
  Search,
  Star,
  Download,
  DollarSign,
  ShoppingCart,
  Tag,
  ChevronRight,
  Filter,
  Sparkles,
  Music,
  CheckCircle,
  Loader2,
  User,
  Percent,
  Grid,
  List,
  X,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Bundle Card Component
const BundleCard = ({ bundle, onSelect, isPurchased, viewMode = 'grid' }) => {
  const discountBadge = bundle.discount_percent > 0 && (
    <div className="absolute top-2 right-2 px-2 py-0.5 bg-emerald-500 text-white text-xs font-bold rounded-full">
      -{bundle.discount_percent}%
    </div>
  );
  
  if (viewMode === 'list') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        onClick={() => onSelect(bundle)}
        className="flex items-center gap-4 p-4 bg-zinc-900/50 border border-zinc-800 rounded-xl hover:border-zinc-700 cursor-pointer transition-colors relative"
      >
        {discountBadge}
        
        <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center flex-shrink-0">
          <Package className="w-8 h-8 text-white" />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-white truncate">{bundle.name}</h3>
            {isPurchased && (
              <span className="flex items-center gap-1 text-xs text-emerald-400 bg-emerald-500/20 px-2 py-0.5 rounded-full">
                <CheckCircle className="w-3 h-3" /> Owned
              </span>
            )}
          </div>
          <p className="text-sm text-zinc-400 truncate">{bundle.description}</p>
          <div className="flex items-center gap-3 mt-1 text-xs text-zinc-500">
            <span className="flex items-center gap-1">
              <Music className="w-3 h-3" />
              {bundle.preset_count} presets
            </span>
            <span className="flex items-center gap-1">
              <Download className="w-3 h-3" />
              {bundle.downloads}
            </span>
            <span className="flex items-center gap-1">
              <Star className="w-3 h-3 text-amber-400" />
              {bundle.rating?.toFixed(1) || '0.0'}
            </span>
          </div>
        </div>
        
        <div className="text-right">
          {bundle.bundle_price > 0 ? (
            <>
              <p className="text-lg font-bold text-white">${bundle.bundle_price.toFixed(2)}</p>
              {bundle.original_price > bundle.bundle_price && (
                <p className="text-xs text-zinc-500 line-through">${bundle.original_price.toFixed(2)}</p>
              )}
            </>
          ) : (
            <p className="text-lg font-bold text-emerald-400">Free</p>
          )}
        </div>
      </motion.div>
    );
  }
  
  // Grid view
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      onClick={() => onSelect(bundle)}
      className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden hover:border-zinc-700 cursor-pointer transition-colors relative group"
    >
      {discountBadge}
      
      {/* Cover Image */}
      <div className="h-32 bg-gradient-to-br from-violet-500/20 to-purple-600/20 flex items-center justify-center relative">
        <Package className="w-12 h-12 text-violet-400" />
        {bundle.preset_count && (
          <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/60 rounded text-xs text-white">
            {bundle.preset_count} presets
          </div>
        )}
      </div>
      
      <div className="p-4">
        <div className="flex items-center gap-2 mb-1">
          <h3 className="font-semibold text-white truncate">{bundle.name}</h3>
          {isPurchased && <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />}
        </div>
        
        <p className="text-sm text-zinc-400 line-clamp-2 mb-3">{bundle.description}</p>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-zinc-500">
            <span className="flex items-center gap-1">
              <Download className="w-3 h-3" />
              {bundle.downloads}
            </span>
            <span className="flex items-center gap-1">
              <Star className="w-3 h-3 text-amber-400" />
              {bundle.rating?.toFixed(1) || '0.0'}
            </span>
          </div>
          
          <div className="text-right">
            {bundle.bundle_price > 0 ? (
              <span className="font-bold text-white">${bundle.bundle_price.toFixed(2)}</span>
            ) : (
              <span className="font-bold text-emerald-400">Free</span>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Bundle Detail Modal
const BundleDetailModal = ({ bundle, onClose, onPurchase, isPurchased, isLoading }) => {
  if (!bundle) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-zinc-900 border border-zinc-800 rounded-2xl max-w-lg w-full max-h-[80vh] overflow-hidden"
      >
        {/* Header */}
        <div className="h-40 bg-gradient-to-br from-violet-500/30 to-purple-600/30 relative flex items-center justify-center">
          <Package className="w-20 h-20 text-violet-400" />
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 bg-black/40 rounded-full hover:bg-black/60"
          >
            <X className="w-5 h-5" />
          </button>
          {bundle.discount_percent > 0 && (
            <div className="absolute bottom-4 left-4 px-3 py-1 bg-emerald-500 text-white text-sm font-bold rounded-full flex items-center gap-1">
              <Percent className="w-4 h-4" />
              {bundle.discount_percent}% OFF
            </div>
          )}
        </div>
        
        {/* Content */}
        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(80vh - 10rem)' }}>
          <h2 className="text-2xl font-bold text-white mb-2">{bundle.name}</h2>
          
          <div className="flex items-center gap-3 mb-4 text-sm text-zinc-400">
            <span className="flex items-center gap-1">
              <User className="w-4 h-4" />
              {bundle.creator_name || 'Unknown'}
            </span>
            <span className="flex items-center gap-1">
              <Download className="w-4 h-4" />
              {bundle.downloads} downloads
            </span>
            <span className="flex items-center gap-1">
              <Star className="w-4 h-4 text-amber-400" />
              {bundle.rating?.toFixed(1) || '0.0'} ({bundle.rating_count || 0})
            </span>
          </div>
          
          <p className="text-zinc-400 mb-6">{bundle.description}</p>
          
          {/* Presets included */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-zinc-300 mb-3">Included Presets ({bundle.preset_count})</h3>
            <div className="grid grid-cols-2 gap-2">
              {(bundle.presets || []).slice(0, 8).map((preset, i) => (
                <div key={i} className="flex items-center gap-2 px-3 py-2 bg-zinc-800/50 rounded-lg">
                  <span className="text-sm">{preset.icon || '💾'}</span>
                  <span className="text-sm text-zinc-300 truncate">{preset.name}</span>
                </div>
              ))}
              {(bundle.presets || []).length > 8 && (
                <div className="flex items-center justify-center px-3 py-2 bg-zinc-800/50 rounded-lg text-sm text-zinc-500">
                  +{bundle.presets.length - 8} more
                </div>
              )}
            </div>
          </div>
          
          {/* Tags */}
          {bundle.tags && bundle.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-6">
              {bundle.tags.map((tag, i) => (
                <span key={i} className="px-2 py-1 bg-zinc-800 rounded-full text-xs text-zinc-400">
                  {tag}
                </span>
              ))}
            </div>
          )}
          
          {/* Pricing */}
          <div className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-xl mb-4">
            <div>
              <p className="text-sm text-zinc-400">Bundle Price</p>
              <div className="flex items-center gap-3">
                {bundle.bundle_price > 0 ? (
                  <>
                    <span className="text-2xl font-bold text-white">${bundle.bundle_price.toFixed(2)}</span>
                    {bundle.original_price > bundle.bundle_price && (
                      <span className="text-lg text-zinc-500 line-through">${bundle.original_price.toFixed(2)}</span>
                    )}
                  </>
                ) : (
                  <span className="text-2xl font-bold text-emerald-400">Free</span>
                )}
              </div>
            </div>
            <div className="text-right text-sm text-zinc-500">
              <p>${((bundle.original_price || 0) / (bundle.preset_count || 1)).toFixed(2)}/preset value</p>
            </div>
          </div>
          
          {/* Action Button */}
          {isPurchased ? (
            <Button disabled className="w-full bg-zinc-700 text-zinc-400 cursor-default">
              <CheckCircle className="w-4 h-4 mr-2" />
              Already Owned
            </Button>
          ) : (
            <Button
              onClick={() => onPurchase(bundle)}
              disabled={isLoading}
              className="w-full bg-violet-600 hover:bg-violet-500"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : bundle.bundle_price > 0 ? (
                <ShoppingCart className="w-4 h-4 mr-2" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              {isLoading ? 'Processing...' : bundle.bundle_price > 0 ? `Buy for $${bundle.bundle_price.toFixed(2)}` : 'Get for Free'}
            </Button>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

// Main Bundles Browser Component
export const PresetBundlesBrowser = ({ userId, onBundlePurchased }) => {
  const [bundles, setBundles] = useState([]);
  const [purchasedIds, setPurchasedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('downloads');
  const [priceFilter, setPriceFilter] = useState('all');
  const [selectedBundle, setSelectedBundle] = useState(null);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Fetch bundles
  const fetchBundles = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        sort_by: sortBy,
        price_filter: priceFilter,
        search: searchQuery,
        page: page.toString(),
        limit: '12'
      });
      
      const response = await fetch(`${API_URL}/api/preset-bundles/?${params}`);
      if (response.ok) {
        const data = await response.json();
        setBundles(data.bundles || []);
        setTotalPages(data.total_pages || 1);
      }
    } catch (error) {
      console.error('Failed to fetch bundles:', error);
    } finally {
      setLoading(false);
    }
  }, [sortBy, priceFilter, searchQuery, page]);
  
  // Fetch purchased bundles
  const fetchPurchased = useCallback(async () => {
    if (!userId) return;
    try {
      const response = await fetch(`${API_URL}/api/preset-bundles/purchased?user_id=${userId}`);
      if (response.ok) {
        const data = await response.json();
        const ids = new Set((data.bundles || []).map(b => b.id));
        setPurchasedIds(ids);
      }
    } catch (error) {
      console.error('Failed to fetch purchased bundles:', error);
    }
  }, [userId]);
  
  useEffect(() => {
    fetchBundles();
  }, [fetchBundles]);
  
  useEffect(() => {
    fetchPurchased();
  }, [fetchPurchased]);
  
  // Handle purchase
  const handlePurchase = async (bundle) => {
    if (!userId) {
      alert('Please log in to purchase bundles');
      return;
    }
    
    setIsPurchasing(true);
    try {
      const response = await fetch(`${API_URL}/api/preset-bundles/${bundle.id}/purchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId })
      });
      
      if (response.ok) {
        const data = await response.json();
        setPurchasedIds(prev => new Set([...prev, bundle.id]));
        setSelectedBundle(null);
        if (onBundlePurchased) {
          onBundlePurchased(data);
        }
        alert(`Successfully added ${data.presets_added} presets to your library!`);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to purchase bundle');
      }
    } catch (error) {
      console.error('Purchase error:', error);
      alert('Failed to process purchase');
    } finally {
      setIsPurchasing(false);
    }
  };
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
            <Package className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">Preset Bundles</h2>
            <p className="text-sm text-zinc-400">Save big with curated preset packs</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setViewMode('grid')}
            className={viewMode === 'grid' ? 'text-white' : 'text-zinc-500'}
          >
            <Grid className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setViewMode('list')}
            className={viewMode === 'list' ? 'text-white' : 'text-zinc-500'}
          >
            <List className="w-4 h-4" />
          </Button>
        </div>
      </div>
      
      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search bundles..."
            className="pl-10 bg-zinc-900/50 border-zinc-800"
          />
        </div>
        
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="px-3 py-2 bg-zinc-900/50 border border-zinc-800 rounded-lg text-sm text-white"
        >
          <option value="downloads">Most Popular</option>
          <option value="rating">Top Rated</option>
          <option value="newest">Newest</option>
          <option value="price">Price: Low to High</option>
          <option value="discount">Best Deals</option>
        </select>
        
        <select
          value={priceFilter}
          onChange={(e) => setPriceFilter(e.target.value)}
          className="px-3 py-2 bg-zinc-900/50 border border-zinc-800 rounded-lg text-sm text-white"
        >
          <option value="all">All Prices</option>
          <option value="free">Free Only</option>
          <option value="paid">Paid Only</option>
        </select>
      </div>
      
      {/* Bundles Grid/List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-violet-400" />
        </div>
      ) : bundles.length === 0 ? (
        <div className="text-center py-12">
          <Package className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
          <p className="text-zinc-400">No bundles found</p>
          <p className="text-sm text-zinc-500">Try adjusting your filters</p>
        </div>
      ) : (
        <div className={viewMode === 'grid' 
          ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4'
          : 'space-y-3'
        }>
          {bundles.map((bundle) => (
            <BundleCard
              key={bundle.id}
              bundle={bundle}
              onSelect={setSelectedBundle}
              isPurchased={purchasedIds.has(bundle.id)}
              viewMode={viewMode}
            />
          ))}
        </div>
      )}
      
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Previous
          </Button>
          <span className="text-sm text-zinc-400">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Next
          </Button>
        </div>
      )}
      
      {/* Detail Modal */}
      <AnimatePresence>
        {selectedBundle && (
          <BundleDetailModal
            bundle={selectedBundle}
            onClose={() => setSelectedBundle(null)}
            onPurchase={handlePurchase}
            isPurchased={purchasedIds.has(selectedBundle.id)}
            isLoading={isPurchasing}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default PresetBundlesBrowser;
